import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import type { ComponentType, ReactNode } from "react";
import { Fragment, memo } from "react";
import * as styles from "./managed-returns-trial-modal.module.css";

export const StepHeading = memo(function StepHeading({
  eyebrow,
  title,
  intro,
}: {
  eyebrow?: string;
  title: ReactNode;
  intro?: ReactNode;
}) {
  return (
    <div className={styles.heading}>
      {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
      <h1 className={styles.stepTitle}>{title}</h1>
      {intro && <p className={styles.intro}>{intro}</p>}
    </div>
  );
});

export const SubHeading = memo(function SubHeading({
  children,
}: {
  children: ReactNode;
}) {
  return <h2 className={styles.subHeading}>{children}</h2>;
});

export const SelectableCard = memo(function SelectableCard({
  title,
  description,
  badge,
  selected,
  onSelect,
}: {
  title: string;
  description: string;
  badge?: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      aria-pressed={selected}
      className={
        selected
          ? `${styles.selectableCard} ${styles.selectableCardSelected}`
          : styles.selectableCard
      }
      onClick={onSelect}
      type="button"
    >
      <span className={styles.choiceText}>
        <span className={styles.choiceTitleRow}>
          <span className={styles.choiceTitle}>{title}</span>
          {badge && <RedoBadge color="blue" size="sm" text={badge} />}
        </span>
        <span className={styles.choiceDescription}>{description}</span>
      </span>
      <span
        className={
          selected
            ? `${styles.radioDot} ${styles.radioDotSelected}`
            : styles.radioDot
        }
      >
        {selected && <CheckIcon className={styles.radioDotSvg} />}
      </span>
    </button>
  );
});

export const FieldCard = memo(function FieldCard({
  title,
  titleAccessory,
  description,
  children,
}: {
  title: string;
  titleAccessory?: ReactNode;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.fieldCard}>
      <div className={styles.fieldHeader}>
        <div className={styles.inlineTitle}>
          <h3 className={styles.fieldTitle}>{title}</h3>
          {titleAccessory}
        </div>
        {description && (
          <p className={styles.fieldDescription}>{description}</p>
        )}
      </div>
      {children}
    </div>
  );
});

export const CheckList = memo(function CheckList({
  items,
}: {
  items: ReactNode[];
}) {
  return (
    <ul className={styles.checkList}>
      {items.map((item, index) => (
        <li className={styles.listItem} key={index}>
          <span className={styles.checkIcon}>
            <CheckIcon className={styles.checkSvg} />
          </span>
          <span className={styles.checkText}>{item}</span>
        </li>
      ))}
    </ul>
  );
});

export const Bullets = memo(function Bullets({ items }: { items: string[] }) {
  return (
    <ul className={styles.bullets}>
      {items.map((item) => (
        <li className={styles.listItem} key={item}>
          <span className={styles.bullet} />
          <span className={styles.bulletText}>{item}</span>
        </li>
      ))}
    </ul>
  );
});

export const Callout = memo(function Callout({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.callout}>
      <span className={styles.calloutLabel}>{label}</span>
      <div className={styles.calloutBody}>{children}</div>
    </div>
  );
});

export const NoteCard = memo(function NoteCard({
  icon,
  title,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  children: ReactNode;
}) {
  const NoteIcon = icon;
  return (
    <div className={styles.noteCard}>
      <div className={styles.inlineTitle}>
        <NoteIcon className={styles.noteIcon} />
        <h4 className={styles.noteTitle}>{title}</h4>
      </div>
      <div className={styles.noteBody}>{children}</div>
    </div>
  );
});

export interface FlowStep {
  icon: ComponentType<{ className?: string }>;
  label: string;
  sub?: string;
}

export const ProcessFlow = memo(function ProcessFlow({
  steps,
}: {
  steps: FlowStep[];
}) {
  return (
    <div className={styles.flow}>
      {steps.map((flowStep, index) => {
        const StepIcon = flowStep.icon;
        return (
          <Fragment key={flowStep.label}>
            <div className={styles.flowStep}>
              <span className={styles.flowIcon}>
                <StepIcon />
              </span>
              <span className={styles.flowLabel}>{flowStep.label}</span>
              {flowStep.sub && (
                <span className={styles.flowSub}>{flowStep.sub}</span>
              )}
            </div>
            {index < steps.length - 1 && (
              <span className={styles.flowArrow}>
                <ArrowRightIcon />
              </span>
            )}
          </Fragment>
        );
      })}
    </div>
  );
});
